import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, X, RotateCcw } from 'lucide-react';

export default function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setError('No se pudo acceder a la cámara. Por favor, verifica los permisos.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0);
      setCaptured(true);
    }
  };

  const handleRetake = () => {
    setCaptured(false);
  };

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      onCapture(blob);
    }, 'image/jpeg', 0.95);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" data-testid="camera-modal">
      <Card className="w-full max-w-2xl mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-slate-900">Capturar Foto</h2>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-camera-button">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={onClose}>Cerrar</Button>
          </div>
        ) : (
          <>
            <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className={`w-full h-full object-cover ${captured ? 'hidden' : ''}`}
                data-testid="camera-video"
              />
              <canvas
                ref={canvasRef}
                className={`w-full h-full object-cover ${captured ? '' : 'hidden'}`}
                data-testid="camera-canvas"
              />
            </div>

            <div className="flex justify-center space-x-3">
              {!captured ? (
                <>
                  <Button variant="outline" onClick={onClose} data-testid="cancel-capture-button">
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCapture}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="capture-button"
                  >
                    <Camera className="mr-2 h-4 w-4" />
                    Capturar
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={handleRetake} data-testid="retake-button">
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Repetir
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="confirm-capture-button"
                  >
                    Confirmar
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
